import { MeetingFormData, ResolvedMatch, Employee } from '../interfaces/interface';
import { logger } from 'src/logger';
import { LEADER_EA_EMAIL_DATA } from '../constants/EAHuddleConstants';

/**
 * Service for handling meeting-related business logic and operations
 */
export class MeetingService {
  private static readonly OUTLOOK_URL_LIMIT = 25000;

  /**
   * Checks if an email address is a custom EA email from the constant data.
   * Custom emails from the constant should be formatted without names for Outlook compatibility.
   */
  private static isCustomEAEmail(email: string): boolean {
    return LEADER_EA_EMAIL_DATA.some((leader) => leader.eaEmailAddress === email);
  }

  /**
   * Formats an EA email for Outlook. Custom emails are formatted without names.
   */
  private static formatEAEmail(ea: { name: string; email: string }, useQuotes: boolean = false): string {
    if (this.isCustomEAEmail(ea.email)) {
      return ea.email;
    }
    return useQuotes ? `"${ea.name}" <${ea.email}>` : `${ea.name} <${ea.email}>`;
  }

  static canCreateMeeting(resolvedAttendees: ResolvedMatch[]): {
    canCreate: boolean;
    issues: string[];
  } {
    const issues: string[] = [];

    const requiredAttendees = resolvedAttendees.filter((a) => a.priority === 'required');

    if (requiredAttendees.length === 0) {
      issues.push('At least one required attendee is needed');
    }

    const unresolvedRequired = requiredAttendees.filter((a) => !a.resolvedEmail);
    if (unresolvedRequired.length > 0) {
      issues.push(`${unresolvedRequired.length} required attendees need email resolution`);
    }

    return {
      canCreate: issues.length === 0,
      issues
    };
  }

  static buildAttendeeData(resolvedAttendees: ResolvedMatch[]) {
    const requiredPeople = resolvedAttendees
      .filter((a) => a.priority === 'required' && a.resolvedEmail)
      .map((a) => ({
        name: a.editedName || a.selectedEmployee?.displayName || a.selectedEmployee?.employee_name || a.inputName,
        email: a.resolvedEmail!
      }));

    const optionalPeople = resolvedAttendees
      .filter((a) => a.priority === 'optional' && a.resolvedEmail)
      .map((a) => ({
        name: a.editedName || a.selectedEmployee?.displayName || a.selectedEmployee?.employee_name || a.inputName,
        email: a.resolvedEmail!
      }));

    // Deduplicate by email - prioritize required over optional
    const allEmails = new Set<string>();
    const finalRequiredWithNames: Array<{ name: string; email: string }> = [];
    const finalOptionalWithNames: Array<{ name: string; email: string }> = [];

    for (const person of requiredPeople) {
      if (!allEmails.has(person.email.toLowerCase())) {
        allEmails.add(person.email.toLowerCase());
        finalRequiredWithNames.push(person);
      }
    }

    for (const person of optionalPeople) {
      if (!allEmails.has(person.email.toLowerCase())) {
        allEmails.add(person.email.toLowerCase());
        finalOptionalWithNames.push(person);
      }
    }

    return {
      requiredEmails: finalRequiredWithNames.map((p) => p.email),
      optionalEmails: finalOptionalWithNames.map((p) => p.email),
      requiredWithNames: finalRequiredWithNames,
      optionalWithNames: finalOptionalWithNames
    };
  }

  private static buildOutlookDeeplinkUrl(baseUrl: string, params: URLSearchParams, bodyContent: string | undefined, urlType: string): string {
    // Build full URL
    let finalUrl = `${baseUrl}?${params.toString()}`;

    // If URL exceeds Outlook's limit and there's body content, replace with placeholder
    if (finalUrl.length > MeetingService.OUTLOOK_URL_LIMIT && bodyContent) {
      const originalLength = finalUrl.length;
      const placeholderMessage = 'IMPORTANT: Please copy and paste the meeting description here as it was too long to include automatically.';
      params.set('body', placeholderMessage);
      finalUrl = `${baseUrl}?${params.toString()}`;

      logger.warn(
        `${urlType} URL exceeded ${MeetingService.OUTLOOK_URL_LIMIT} characters ` +
          `(Limit: ${MeetingService.OUTLOOK_URL_LIMIT}, Actual: ${originalLength}). ` +
          `Description replaced with placeholder.`
      );
    }

    return finalUrl;
  }

  static generateOutlookWebUrl(formData: MeetingFormData, resolvedAttendees: ResolvedMatch[]): string {
    const attendeeData = this.buildAttendeeData(resolvedAttendees);
    const bodyContent = formData.description;

    // Construct Outlook Web App URL with names and emails
    const baseUrl = 'https://outlook.office365.com/calendar/deeplink/compose';
    const params = new URLSearchParams();

    if (formData.subject) {
      params.append('subject', formData.subject);
    }

    // Use name + email format if available, otherwise fall back to just emails
    if (attendeeData.requiredWithNames && attendeeData.requiredWithNames.length > 0) {
      const formattedRequired = attendeeData.requiredWithNames.map((person) => `"${person.name}" <${person.email}>`);
      params.append('to', formattedRequired.join(','));
    } else if (attendeeData.requiredEmails.length > 0) {
      params.append('to', attendeeData.requiredEmails.join(','));
    }

    if (attendeeData.optionalWithNames && attendeeData.optionalWithNames.length > 0) {
      const formattedOptional = attendeeData.optionalWithNames.map((person) => `"${person.name}" <${person.email}>`);
      params.append('cc', formattedOptional.join(','));
    } else if (attendeeData.optionalEmails.length > 0) {
      params.append('cc', attendeeData.optionalEmails.join(','));
    }

    // Use updated HTML content to preserve table formatting in Outlook
    if (bodyContent) {
      params.append('body', bodyContent);
    }

    return this.buildOutlookDeeplinkUrl(baseUrl, params, bodyContent, 'Outlook Calendar');
  }

  static collectExecutiveAssistants(resolvedAttendees: ResolvedMatch[]): Array<{ name: string; email: string }> {
    const allEAs: Array<{ name: string; email: string }> = [];
    const seenEmails = new Set<string>();

    resolvedAttendees.forEach((attendee) => {
      if (attendee.selectedExecutiveAssistant) {
        const email = attendee.selectedExecutiveAssistant.employee_internal_email_address?.toLowerCase();
        if (email && !seenEmails.has(email)) {
          seenEmails.add(email);
          allEAs.push({
            name: attendee.selectedExecutiveAssistant.employee_name,
            email: attendee.selectedExecutiveAssistant.employee_internal_email_address
          });
        }
      }
    });

    return allEAs;
  }

  static generateEASubject(formData: MeetingFormData): string {
    return `Meeting Request - ${formData.subject}`;
  }

  /**
   * Generate EA email body with manual message
   * TODO: Revisit this function later when we onboard to relevant APIs that maintain word formatting
   */
  static generateEABody(): string {
    const manualMessage = `Hi everyone, 

Sending this note to align on availability for the meeting below, can you please confirm their scheduling windows so I can consolidate and send the final invite?`;

    return manualMessage;
  }

  static formatAttendeesForCopy(resolvedAttendees: ResolvedMatch[]): string {
    const attendeeData = this.buildAttendeeData(resolvedAttendees);
    const allAttendees = [...attendeeData.requiredWithNames, ...attendeeData.optionalWithNames];

    return allAttendees.map((person) => `${person.name} <${person.email}>`).join('; ');
  }

  static formatExecutiveAssistantsForCopy(resolvedAttendees: ResolvedMatch[]): string {
    const executiveAssistants = this.collectExecutiveAssistants(resolvedAttendees);

    return executiveAssistants.map((ea) => this.formatEAEmail(ea)).join('; ');
  }

  private static formatEmployeeDetails(employee: Employee): string {
    // Combine name and login into "Name (login@)" format
    const nameWithLogin = employee.employee_name
      ? `${employee.employee_name} (${employee.login_name || ''}@)`
      : employee.login_name
      ? `${employee.login_name}@`
      : '';

    return [
      nameWithLogin,
      employee.job_title_name || '',
      employee.job_level_name || '',
      employee.location_city_name || '',
      employee.department_name_current || ''
    ]
      .filter(Boolean)
      .join(', ');
  }

  static formatEmployeeDetailsForCopy(resolvedAttendees: ResolvedMatch[]): string {
    const employeeDetails: string[] = [];

    resolvedAttendees.forEach((attendee) => {
      if (attendee.selectedEmployee) {
        employeeDetails.push(this.formatEmployeeDetails(attendee.selectedEmployee));
      }
    });

    return employeeDetails.join('\n');
  }

  static formatExecutiveAssistantDetailsForCopy(resolvedAttendees: ResolvedMatch[]): string {
    const eaDetails: string[] = [];
    const seenLogins = new Set<string>();

    resolvedAttendees.forEach((attendee) => {
      if (attendee.selectedExecutiveAssistant) {
        const ea = attendee.selectedExecutiveAssistant;
        const login = ea.login_name;

        // Only add if not already seen (with fallback for missing logins)
        if (!seenLogins.has(login)) {
          seenLogins.add(login);
          eaDetails.push(this.formatEmployeeDetails(ea));
        }
      }
    });

    return eaDetails.join('\n');
  }

  static generateEAMailtoUrl(formData: MeetingFormData, resolvedAttendees: ResolvedMatch[]): string {
    const executiveAssistants = this.collectExecutiveAssistants(resolvedAttendees);

    if (executiveAssistants.length === 0) {
      throw new Error('No executive assistants found');
    }

    const eaSubject = this.generateEASubject(formData);
    const bodyContent = this.generateEABody();

    // Format EAs with name and email, except for custom emails which should be email-only for Outlook compatibility
    const formattedEAs = executiveAssistants.map((ea) => this.formatEAEmail(ea, true));
    const encodedTo = encodeURIComponent(formattedEAs.join(','));
    const encodedSubject = encodeURIComponent(eaSubject);
    const encodedBody = encodeURIComponent(bodyContent);

    // Manually construct URL with proper encoding (spaces become %20, not +)
    const baseUrl = 'https://outlook.office365.com/mail/deeplink/compose';
    let finalUrl = `${baseUrl}?subject=${encodedSubject}&to=${encodedTo}&body=${encodedBody}`;

    // Check URL limit and replace body if needed
    if (finalUrl.length > MeetingService.OUTLOOK_URL_LIMIT) {
      const originalLength = finalUrl.length;
      const placeholderMessage = 'IMPORTANT: Please copy and paste the meeting description here as it was too long to include automatically.';
      const encodedPlaceholder = encodeURIComponent(placeholderMessage);
      finalUrl = `${baseUrl}?subject=${encodedSubject}&to=${encodedTo}&body=${encodedPlaceholder}`;

      logger.warn(
        `EA Email URL exceeded ${MeetingService.OUTLOOK_URL_LIMIT} characters ` +
          `(Limit: ${MeetingService.OUTLOOK_URL_LIMIT}, Actual: ${originalLength}). ` +
          `Description replaced with placeholder.`
      );
    }

    return finalUrl;
  }

  static findDuplicateEmails(resolvedAttendees: ResolvedMatch[]): Set<string> {
    const emailCounts = new Map<string, number>();
    const duplicates = new Set<string>();

    resolvedAttendees.forEach((attendee) => {
      const email = attendee.resolvedEmail?.toLowerCase();
      if (email) {
        const count = emailCounts.get(email) || 0;
        emailCounts.set(email, count + 1);
        if (count >= 1) {
          duplicates.add(email);
        }
      }
    });

    return duplicates;
  }

  static getMeetingStatistics(resolvedAttendees: ResolvedMatch[]) {
    const requiredAttendees = resolvedAttendees.filter((a) => a.priority === 'required');
    const optionalAttendees = resolvedAttendees.filter((a) => a.priority === 'optional');
    const resolvedRequired = requiredAttendees.filter((a) => a.resolvedEmail);
    const resolvedOptional = optionalAttendees.filter((a) => a.resolvedEmail);
    const executiveAssistants = this.collectExecutiveAssistants(resolvedAttendees);
    const duplicateEmails = this.findDuplicateEmails(resolvedAttendees);

    return {
      totalAttendees: resolvedAttendees.length,
      requiredCount: requiredAttendees.length,
      optionalCount: optionalAttendees.length,
      resolvedRequiredCount: resolvedRequired.length,
      resolvedOptionalCount: resolvedOptional.length,
      unresolvedRequiredCount: requiredAttendees.length - resolvedRequired.length,
      unresolvedOptionalCount: optionalAttendees.length - resolvedOptional.length,
      executiveAssistantsCount: executiveAssistants.length,
      duplicateEmailsCount: duplicateEmails.size,
      canCreateMeeting: this.canCreateMeeting(resolvedAttendees).canCreate
    };
  }
}
